import React, { useEffect, useState, Fragment } from 'react';
import { TruckIcon, PlusCircleIcon, MinusCircleIcon, CheckCircleIcon, AlertCircleIcon, InfoIcon } from 'lucide-react';
interface TruckingCompany {
  id: string;
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  rating: number;
  truckCount: number;
  isDefault?: boolean;
  rates: {
    [key: string]: number;
  };
}
interface Job {
  id: string;
  jobName: string;
  jobNumber: string;
  date: string;
  material: string;
  numberOfTrucks: string;
  loadLocation: string;
  dumpLocation: string;
  truckType?: string;
  customer?: string;
  assignments?: {
    truckingCompanyId: string;
    truckCount: number;
  }[];
}
interface AssignJobsToTrucksProps {
  jobs: Job[];
  truckingCompanies: TruckingCompany[];
  onAssignJobs: (jobId: string, assignments: {
    truckingCompanyId: string;
    truckCount: number;
  }[]) => void;
}
export const AssignJobsToTrucks = ({
  jobs,
  truckingCompanies,
  onAssignJobs
}: AssignJobsToTrucksProps) => {
  const [assignments, setAssignments] = useState<Record<string, {
    truckingCompanyId: string;
    truckCount: number;
  }[]>>({});
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  // Initialize assignments with default trucking company if not already set
  useEffect(() => {
    const newAssignments = {
      ...assignments
    };
    let updated = false;
    jobs.forEach(job => {
      if (!newAssignments[job.id] && !job.assignments) {
        const defaultCompany = truckingCompanies.find(company => company.isDefault);
        if (defaultCompany) {
          const truckCount = parseInt(job.numberOfTrucks.split(' ')[0]) || 1;
          newAssignments[job.id] = [{
            truckingCompanyId: defaultCompany.id,
            truckCount
          }];
          updated = true;
        }
      } else if (job.assignments && !newAssignments[job.id]) {
        newAssignments[job.id] = job.assignments;
        updated = true;
      }
    });
    if (updated) {
      setAssignments(newAssignments);
    }
  }, [jobs, truckingCompanies]);
  const handleAddTruckingCompany = (jobId: string) => {
    setAssignments(prev => {
      const currentAssignments = [...(prev[jobId] || [])];
      const assignedCompanyIds = currentAssignments.map(a => a.truckingCompanyId);
      const availableCompany = truckingCompanies.find(c => !assignedCompanyIds.includes(c.id));
      if (availableCompany) {
        return {
          ...prev,
          [jobId]: [...currentAssignments, {
            truckingCompanyId: availableCompany.id,
            truckCount: 1
          }]
        };
      }
      return prev;
    });
  };
  const handleRemoveTruckingCompany = (jobId: string, index: number) => {
    setAssignments(prev => {
      const currentAssignments = [...(prev[jobId] || [])];
      currentAssignments.splice(index, 1);
      return {
        ...prev,
        [jobId]: currentAssignments
      };
    });
  };
  const handleChangeCompany = (jobId: string, index: number, companyId: string) => {
    setAssignments(prev => {
      const currentAssignments = [...(prev[jobId] || [])];
      currentAssignments[index] = {
        ...currentAssignments[index],
        truckingCompanyId: companyId
      };
      return {
        ...prev,
        [jobId]: currentAssignments
      };
    });
  };
  const handleChangeTruckCount = (jobId: string, index: number, truckCount: number) => {
    setAssignments(prev => {
      const currentAssignments = [...(prev[jobId] || [])];
      currentAssignments[index] = {
        ...currentAssignments[index],
        truckCount
      };
      return {
        ...prev,
        [jobId]: currentAssignments
      };
    });
  };
  const getTotalAssignedTrucks = (jobId: string) => {
    return (assignments[jobId] || []).reduce((sum, assignment) => sum + assignment.truckCount, 0);
  };
  const getRequiredTrucks = (job: Job) => {
    return parseInt(job.numberOfTrucks.split(' ')[0]) || 0;
  };
  const isJobFullyAssigned = (job: Job) => {
    return getTotalAssignedTrucks(job.id) === getRequiredTrucks(job);
  };
  const handleAssignJob = (jobId: string) => {
    onAssignJobs(jobId, assignments[jobId] || []);
  };
  return <div className="bg-white rounded-md shadow p-6" data-id="element-1153">
      <h2 className="text-xl font-bold mb-4" data-id="element-1154">
        Assign Jobs to Trucking Companies (Direct)
      </h2>
      {jobs.length === 0 ? <div className="bg-gray-50 p-6 rounded-md text-center" data-id="element-1155">
          <TruckIcon size={40} className="mx-auto mb-2 text-gray-400" data-id="element-1156" />
          <p className="text-gray-600" data-id="element-1157">
            No jobs available for direct assignment
          </p>
          <p className="text-sm text-gray-500 mt-1" data-id="element-1158">
            Create new jobs or mark them for direct assignment to get started
          </p>
        </div> : <div className="overflow-x-auto" data-id="element-1159">
          <table className="w-full border-collapse" data-id="element-1160">
            <thead data-id="element-1161">
              <tr className="bg-gray-50" data-id="element-1162">
                <th className="text-left p-3 border-b" data-id="element-1163">Customer</th>
                <th className="text-left p-3 border-b" data-id="element-1164">Job Name</th>
                <th className="text-left p-3 border-b" data-id="element-1165">Job #</th>
                <th className="text-left p-3 border-b" data-id="element-1166">Date</th>
                <th className="text-left p-3 border-b" data-id="element-1167">Material</th>
                <th className="text-left p-3 border-b" data-id="element-1168">Truck Type</th>
                <th className="text-left p-3 border-b" data-id="element-1169">Trucks Needed</th>
                <th className="text-left p-3 border-b" data-id="element-1170">Location</th>
                <th className="text-left p-3 border-b" data-id="element-1171">Assignment Status</th>
                <th className="text-left p-3 border-b" data-id="element-1172">Actions</th>
              </tr>
            </thead>
            <tbody data-id="element-1173">
              {jobs.map(job => <Fragment key={job.id} data-id="element-1174">
                  <tr className="hover:bg-gray-50" data-id="element-1175">
                    <td className="p-3 border-b" data-id="element-1176">
                      <div className="font-medium text-purple-600" data-id="element-1177">
                        {job.customer || 'Internal'}
                      </div>
                    </td>
                    <td className="p-3 border-b" data-id="element-1178">
                      <div className="font-medium" data-id="element-1179">{job.jobName}</div>
                    </td>
                    <td className="p-3 border-b" data-id="element-1180">{job.jobNumber}</td>
                    <td className="p-3 border-b" data-id="element-1181">{job.date}</td>
                    <td className="p-3 border-b" data-id="element-1182">{job.material}</td>
                    <td className="p-3 border-b" data-id="element-1183">
                      <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2 py-1 rounded-full" data-id="element-1184">
                        {job.truckType || 'Not specified'}
                      </span>
                    </td>
                    <td className="p-3 border-b" data-id="element-1185">{job.numberOfTrucks}</td>
                    <td className="p-3 border-b" data-id="element-1186">
                      {job.loadLocation} → {job.dumpLocation}
                    </td>
                    <td className="p-3 border-b" data-id="element-1187">
                      {isJobFullyAssigned(job) ? <span className="flex items-center text-green-600" data-id="element-1188">
                          <CheckCircleIcon size={16} className="mr-1" data-id="element-1189" />
                          Fully Assigned
                        </span> : <span className="flex items-center text-orange-500" data-id="element-1190">
                          <AlertCircleIcon size={16} className="mr-1" data-id="element-1191" />
                          {getTotalAssignedTrucks(job.id)}/
                          {getRequiredTrucks(job)} Trucks Assigned
                        </span>}
                    </td>
                    <td className="p-3 border-b" data-id="element-1192">
                      <div className="flex gap-2" data-id="element-1193">
                        <button className="text-blue-600 hover:text-blue-800" onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)} data-id="element-1194">
                          {expandedJob === job.id ? 'Hide' : 'Assign'}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedJob === job.id && <tr data-id="element-1195">
                      <td colSpan={10} className="p-0 border-b" data-id="element-1196">
                        <div className="bg-gray-50 p-4" data-id="element-1197">
                          <div className="mb-4 flex items-center justify-between" data-id="element-1198">
                            <div data-id="element-1199">
                              <h3 className="font-medium" data-id="element-1200">
                                Trucking Company Assignments (Direct)
                              </h3>
                              {job.customer && <p className="text-sm text-gray-600 mt-1" data-id="element-1201">
                                  Customer:{' '}
                                  <span className="font-medium text-purple-600" data-id="element-1202">
                                    {job.customer}
                                  </span>
                                </p>}
                            </div>
                            <div className="flex items-center gap-2" data-id="element-1203">
                              <span className="text-sm text-gray-600" data-id="element-1204">
                                {getTotalAssignedTrucks(job.id)}/
                                {getRequiredTrucks(job)} trucks assigned
                              </span>
                              <button className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm" onClick={() => handleAddTruckingCompany(job.id)} disabled={(assignments[job.id] || []).length >= truckingCompanies.length} data-id="element-1205">
                                <PlusCircleIcon size={16} data-id="element-1206" />
                                Add Company
                              </button>
                            </div>
                          </div>
                          {(assignments[job.id] || []).map((assignment, index) => <div key={index} className="bg-white p-3 rounded-md mb-2 border" data-id="element-1207">
                                <div className="flex items-center gap-4" data-id="element-1208">
                                  <div className="flex-grow" data-id="element-1209">
                                    <label className="block text-sm font-medium mb-1" data-id="element-1210">
                                      Trucking Company
                                    </label>
                                    <select value={assignment.truckingCompanyId} onChange={e => handleChangeCompany(job.id, index, e.target.value)} className="w-full border border-gray-300 rounded-md p-2" data-id="element-1211">
                                      {truckingCompanies.map(company => {
                            const isAlreadySelected = assignments[job.id]?.some((a, i) => a.truckingCompanyId === company.id && i !== index);
                            const jobTruckType = job.truckType || '';
                            const rate = company.rates?.[jobTruckType] || company.rates?.['default'] || 0;
                            return <option key={company.id} value={company.id} disabled={isAlreadySelected} data-id="element-1212">
                                            {company.name} (
                                            {company.contactPerson}) - $
                                            {rate.toFixed(2)}/hr
                                          </option>;
                          })}
                                    </select>
                                  </div>
                                  <div className="w-48" data-id="element-1213">
                                    <label className="block text-sm font-medium mb-1" data-id="element-1214">
                                      Number of Trucks
                                    </label>
                                    <input type="number" min="1" max={getRequiredTrucks(job) - getTotalAssignedTrucks(job.id) + assignment.truckCount} value={assignment.truckCount} onChange={e => handleChangeTruckCount(job.id, index, parseInt(e.target.value) || 0)} className="w-full border border-gray-300 rounded-md p-2" data-id="element-1215" />
                                  </div>
                                  <div className="flex items-end" data-id="element-1216">
                                    <button className="p-2 text-red-500 hover:text-red-700" onClick={() => handleRemoveTruckingCompany(job.id, index)} data-id="element-1217">
                                      <MinusCircleIcon size={20} data-id="element-1218" />
                                    </button>
                                  </div>
                                </div>
                              </div>)}
                          {isJobFullyAssigned(job) ? <div className="mt-4 flex justify-end" data-id="element-1219">
                              <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center gap-2" onClick={() => handleAssignJob(job.id)} data-id="element-1220">
                                <TruckIcon size={16} data-id="element-1221" />
                                Send Job to Trucking Companies (Direct)
                              </button>
                            </div> : <div className="mt-4 bg-blue-50 p-3 rounded-md flex items-start gap-2" data-id="element-1222">
                              <InfoIcon size={18} className="text-blue-500 flex-shrink-0 mt-0.5" data-id="element-1223" />
                              <div className="text-sm text-blue-800" data-id="element-1224">
                                <p data-id="element-1225">
                                  Please assign all {getRequiredTrucks(job)}{' '}
                                  trucks before sending the job.
                                </p>
                                <p className="mt-1" data-id="element-1226">
                                  You can split the job among multiple trucking
                                  companies.
                                </p>
                              </div>
                            </div>}
                        </div>
                      </td>
                    </tr>}
                </Fragment>)}
            </tbody>
          </table>
        </div>}
    </div>;
};